import React, { useState, useEffect } from 'react';
import {
  GitCommit,
  GitBranch,
  Calendar,
  Layers,
  Sparkles,
  CheckCircle2,
  Clock,
  ShieldCheck,
  X,
  History,
  Tag,
  ArrowUpRight,
  Info
} from 'lucide-react';
import {
  getAppVersionInfo,
  APP_RELEASE_HISTORY,
  getStoredVersionSnapshots,
  recordVersionSnapshot,
  MAX_RETAINED_VERSIONS,
  AppVersionSnapshot
} from '../utils/versionManager';
import { Language } from '../types/kundali';

interface AppVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang?: Language;
  savedKundalisCount?: number;
}

export const AppVersionModal: React.FC<AppVersionModalProps> = ({
  isOpen,
  onClose,
  lang = 'en',
  savedKundalisCount = 0
}) => {
  const isHi = lang === 'hi';
  const versionInfo = getAppVersionInfo();
  const [snapshots, setSnapshots] = useState<AppVersionSnapshot[]>([]);
  const [activeTab, setActiveTab] = useState<'releases' | 'snapshots'>('releases');
  const [snapshotSuccess, setSnapshotSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSnapshots(getStoredVersionSnapshots());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTakeSnapshot = () => {
    const updated = recordVersionSnapshot(
      `Manual checkpoint by user (${savedKundalisCount} horoscopes)`,
      savedKundalisCount
    );
    setSnapshots(updated);
    setSnapshotSuccess(true);
    setTimeout(() => setSnapshotSuccess(false), 3000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-fadeIn">
      <div
        className="relative w-full max-w-2xl max-h-[90vh] bg-white dark:bg-astro-card rounded-2xl shadow-2xl border border-astro-border flex flex-col overflow-hidden text-astro-text"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-astro-border bg-astro-card/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-astro-primary/10 text-astro-primary flex items-center justify-center font-bold">
              <Tag className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-base sm:text-lg text-astro-primary font-serif">
                  {isHi ? 'एप्लिकेशन संस्करण एवं रिलीज़ इतिहास' : 'App Version & Release History'}
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-astro-primary/10 text-astro-primary border border-astro-primary/20">
                  {versionInfo.displayVersion}
                </span>
              </div>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'स्वचालित गिट कमिट स्टाम्प एवं संस्करण इतिहास'
                  : 'Automated Git SHA stamping & complete version history'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-astro-secondary-text hover:text-astro-text hover:bg-astro-bg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Current Live Build Badge Card */}
        <div className="p-4 sm:p-5 bg-astro-bg/50 border-b border-astro-border space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 p-3.5 rounded-xl bg-white dark:bg-astro-card border border-astro-border shadow-2xs">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-astro-secondary-text">
                  {isHi ? 'सक्रिय संस्करण' : 'Active Build'}
                </span>
                <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300">
                  v{versionInfo.version}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-astro-secondary-text flex-wrap">
                <span className="flex items-center gap-1 font-mono">
                  <GitCommit className="w-3.5 h-3.5 text-astro-primary" />
                  <strong>{versionInfo.gitCommit}</strong>
                </span>
                <span className="flex items-center gap-1">
                  <GitBranch className="w-3.5 h-3.5 text-astro-secondary" />
                  <span>{versionInfo.gitBranch}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-astro-secondary-text" />
                  <span>{versionInfo.formattedBuildDate}</span>
                </span>
              </div>
            </div>

            <button
              onClick={handleTakeSnapshot}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-astro-primary text-white hover:bg-astro-primary-dark transition-all flex items-center gap-1.5 shadow-2xs shrink-0 cursor-pointer"
            >
              {snapshotSuccess ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                  <span>{isHi ? 'स्नैपशॉट सुरक्षित!' : 'Snapshot Saved!'}</span>
                </>
              ) : (
                <>
                  <History className="w-3.5 h-3.5" />
                  <span>{isHi ? 'वर्शन स्नैपशॉट लें' : 'Save Version Snapshot'}</span>
                </>
              )}
            </button>
          </div>

          {/* Version Info Notice */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-astro-teal-tint/50 border border-astro-primary/20 text-astro-primary text-[11px]">
            <Info className="w-4 h-4 shrink-0" />
            <span>
              {isHi
                ? `संस्करण प्रतिधारण नीति: सिस्टम स्वचालित रूप से केवल नवीनतम ${MAX_RETAINED_VERSIONS} संस्करणों को ही सुरक्षित रखता है।`
                : `Version Retention Policy: The system automatically retains strictly the last ${MAX_RETAINED_VERSIONS} application releases and snapshots.`}
            </span>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center px-5 border-b border-astro-border bg-astro-card/40">
          <button
            onClick={() => setActiveTab('releases')}
            className={`py-2.5 px-4 text-xs font-bold border-b-2 transition-all cursor-pointer ${
              activeTab === 'releases'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            {isHi ? 'रिलीज़ इतिहास (Release History)' : 'Release History'}
          </button>
          <button
            onClick={() => setActiveTab('snapshots')}
            className={`py-2.5 px-4 text-xs font-bold border-b-2 transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'snapshots'
                ? 'border-astro-primary text-astro-primary'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <span>{isHi ? 'स्थानीय स्नैपशॉट' : 'Local Snapshots'}</span>
            <span className="px-1.5 py-0.2 rounded-full text-[9px] bg-astro-border text-astro-text font-mono">
              {snapshots.length}
            </span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-5 overflow-y-auto max-h-[50vh] space-y-4 custom-scrollbar">
          {activeTab === 'releases' ? (
            <div className="space-y-3">
              {APP_RELEASE_HISTORY.map((rel, index) => (
                <div
                  key={rel.version}
                  className={`p-4 rounded-xl border transition-all ${
                    index === 0
                      ? 'bg-astro-primary/5 border-astro-primary/30 ring-1 ring-astro-primary/20'
                      : 'bg-white dark:bg-astro-card border-astro-border'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 pb-2 border-b border-astro-border/50">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold font-mono ${
                        index === 0
                          ? 'bg-astro-primary text-white'
                          : 'bg-astro-card text-astro-primary border border-astro-primary/30'
                      }`}>
                        v{rel.version}
                      </span>
                      <h4 className="font-bold text-sm text-astro-text">
                        {isHi ? rel.titleHi : rel.title}
                      </h4>
                      {index === 0 && (
                        <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300">
                          {isHi ? 'वर्तमान' : 'Latest'}
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-astro-secondary-text font-mono">
                      {rel.releaseDate}
                    </span>
                  </div>

                  <ul className="mt-2.5 space-y-1 text-xs text-astro-secondary-text">
                    {(isHi ? rel.highlightsHi : rel.highlights).map((hl, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-astro-primary font-bold mt-0.5">•</span>
                        <span>{hl}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {snapshots.length === 0 ? (
                <div className="text-center py-8 text-astro-secondary-text text-xs space-y-2">
                  <History className="w-8 h-8 mx-auto opacity-40" />
                  <p>{isHi ? 'कोई स्नैपशॉट नहीं मिला। नया स्नैपशॉट लेने के लिए ऊपर बटन दबाएं।' : 'No local snapshots saved yet. Click "Save Version Snapshot" above to record one.'}</p>
                </div>
              ) : (
                snapshots.map((snap, i) => (
                  <div
                    key={snap.id}
                    className="p-3.5 rounded-xl border border-astro-border bg-white dark:bg-astro-card flex items-center justify-between gap-3 text-xs"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-astro-primary font-mono">
                          v{snap.version}
                        </span>
                        <span className="font-mono text-astro-secondary-text bg-astro-bg px-1.5 py-0.5 rounded border border-astro-border text-[10px]">
                          {snap.gitCommit}
                        </span>
                        <span className="text-[10px] text-astro-secondary-text">
                          {new Date(snap.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-[11px] text-astro-secondary-text">{snap.notes}</p>
                    </div>

                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-astro-card border border-astro-border shrink-0">
                      #{i + 1}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-astro-border bg-astro-card/80 text-xs">
          <span className="text-astro-secondary-text text-[11px]">
            {isHi ? 'AstroVeda स्वचालित रिलीज़ मैनेजर' : 'AstroVeda Automated Release Manager'}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg font-semibold bg-astro-card text-astro-text border border-astro-border hover:bg-astro-bg transition-colors cursor-pointer"
          >
            {isHi ? 'बंद करें' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};
